import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Switch,
  FormControlLabel,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  type SelectChangeEvent,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { manualActionApi } from '../../services/api';
import { useSearchParams } from 'react-router-dom';
import ReactJson from '@microlink/react-json-view';

interface ManualAction {
  id: number;
  action_type: string;
  key: string;
  data: string;
  done: boolean;
  cancelled: boolean;
  created_at: string;
  updated_at: string;
}

const ManualActions: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [manualActions, setManualActions] = useState<ManualAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [doneFilter, setDoneFilter] = useState<string>(searchParams.get('done') || '');
  const [cancelledFilter, setCancelledFilter] = useState<string>(searchParams.get('cancelled') || '');
  const [actionTypeFilter, setActionTypeFilter] = useState<string>(searchParams.get('action_type') || '');
  const [actionTypes, setActionTypes] = useState<string[]>([]);

  useEffect(() => {
    const done = searchParams.get('done');
    const cancelled = searchParams.get('cancelled');
    const actionType = searchParams.get('action_type');
    setDoneFilter(done || '');
    setCancelledFilter(cancelled || '');
    setActionTypeFilter(actionType || '');
  }, [searchParams]);

  useEffect(() => {
    const fetchActionTypes = async () => {
      try {
        const types = await manualActionApi.getActionTypes();
        setActionTypes(types);
      } catch (error) {
        console.error('Error fetching action types:', error);
      }
    };
    fetchActionTypes();
  }, []);

  const fetchManualActions = async () => {
    try {
      setLoading(true);
      const response = await manualActionApi.getAll({
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
        search: searchTerm,
        done: doneFilter,
        cancelled: cancelledFilter,
        action_type: actionTypeFilter,
      });
      setManualActions(response.results);
    } catch (error) {
      console.error('Error fetching manual actions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchManualActions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel, searchTerm, doneFilter, cancelledFilter, actionTypeFilter]);

  const handleActionTypeFilterChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    setActionTypeFilter(value);
    setSearchParams(prev => {
      if (value) {
        prev.set('action_type', value);
      } else {
        prev.delete('action_type');
      }
      return prev;
    });
  };

  const handleDoneFilterChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    setDoneFilter(value);
    setSearchParams(prev => {
      if (value) {
        prev.set('done', value);
      } else {
        prev.delete('done');
      }
      return prev;
    });
  };

  const handleCancelledFilterChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    setCancelledFilter(value);
    setSearchParams(prev => {
      if (value) {
        prev.set('cancelled', value);
      } else {
        prev.delete('cancelled');
      }
      return prev;
    });
  };

  const handleStatusChange = async (id: number, field: 'done' | 'cancelled', value: boolean) => {
    try {
      await manualActionApi.update(id, { [field]: value });
      fetchManualActions();
    } catch (error) {
      console.error(`Error updating ${field} status:`, error);
    }
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'action_type', headerName: 'Action Type', width: 200 },
    { field: 'key', headerName: 'Key', width: 150 },
    {
      field: 'data',
      headerName: 'Data',
      width: 400,
      renderCell: (params) => {
        try {
          const parsedData = params.value;
          return (
            <Box sx={{ maxHeight: '100px', overflow: 'auto', width: '100%' }}>
              <ReactJson
                // theme='apathy'
                src={parsedData}
                name={false}
                collapsed={1}
                enableClipboard={false}
                displayDataTypes={false}
                style={{ fontSize: '0.6rem', background: 'none' }}
              />
            </Box>
          );
        } catch (error) {
          return <Typography color="error">Invalid JSON</Typography>;
        }
      },
    },
    {
      field: 'done',
      headerName: 'Done',
      width: 100,
      renderCell: (params) => (
        <FormControlLabel
          control={
            <Switch
              checked={params.value}
              onChange={(e) => handleStatusChange(params.row.id, 'done', e.target.checked)}
              size="small"
            />
          }
          label=""
        />
      ),
    },
    {
      field: 'cancelled',
      headerName: 'Cancelled',
      width: 100,
      renderCell: (params) => (
        <FormControlLabel
          control={
            <Switch
              checked={params.value}
              onChange={(e) => handleStatusChange(params.row.id, 'cancelled', e.target.checked)}
              size="small"
            />
          }
          label=""
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      width: 180,
      valueFormatter: (value: any) => format(new Date(value), 'PPpp'),
    },
    {
      field: 'updated_at',
      headerName: 'Updated At',
      width: 180,
      valueFormatter: (value: any) => format(new Date(value), 'PPpp'),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Manual Actions
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            size="small"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Action Type</InputLabel>
            <Select
              value={actionTypeFilter}
              label="Action Type"
              onChange={handleActionTypeFilterChange}
            >
              <MenuItem value="">All</MenuItem>
              {actionTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Done</InputLabel>
            <Select
              value={doneFilter}
              label="Done"
              onChange={handleDoneFilterChange}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="false">Not Done</MenuItem>
              <MenuItem value="true">Done</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Cancelled</InputLabel>
            <Select
              value={cancelledFilter}
              label="Cancelled"
              onChange={handleCancelledFilterChange}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="false">Not Cancelled</MenuItem>
              <MenuItem value="true">Cancelled</MenuItem>
            </Select>
          </FormControl>
          <IconButton onClick={fetchManualActions} color="primary">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      <Paper sx={{ height: 'calc(100vh - 200px)', width: '100%' }}>
        <DataGrid
          rows={manualActions}
          columns={columns}
          loading={loading}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          getRowHeight={() => 'auto'}
        />
      </Paper>
    </Box>
  );
};

export default ManualActions;